import { useRef } from 'react';
import { IconFilterOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, ChipPicker, DatePicker, Text } from '@fragments';

import GranularityPopperContent from '../GranularityPopperContent';

import { DATE_INTERVAL_PRESETS, DATE_INTERVAL_PRESET_MAP, toDisplayDate } from '../../../enums/dateIntervalPresets';
import { CHARGE_STATUSES } from '../../../enums/chargeStatuses';
import { PAYMENT_METHODS } from '../../../enums/paymentMethods';
import { formatGranularity } from '../../../enums/granularityTypes';

import './styles.scss';

export default function FiltersSection({
    dateRange,
    applyPreset,
    applyCustomRange,

    plans,
    planIds,
    setPlanIds,

    chargeStatuses,
    setChargeStatuses,

    chargeMethods,
    setChargeMethods,

    providerFilters,

    granularity,
    setGranularity,

    clearOptionalFilters,
}) {
    const { t } = useTranslation('dashboard');
    const intervalPickerRef = useRef(null);

    const statusOptions = CHARGE_STATUSES.map(s => ({ id: s.id, label: t(s.labelKey) }));
    const methodOptions = PAYMENT_METHODS.map(m => ({ id: m.id, label: t(m.labelKey) }));
    const datePresets = DATE_INTERVAL_PRESETS.map(p => ({ ...p, label: t(p.labelKey) }));

    // The provider filter has no chip, but a shared link can still carry one
    // (`?providers=`) — count it so the clear button surfaces the hidden filter.
    const hasOptionalFilters =
        planIds.length !== plans.length ||
        chargeStatuses.length > 0 ||
        chargeMethods.length > 0 ||
        providerFilters.length > 0;

    function handleDateChipClick(event) {
        intervalPickerRef.current?.open(event.currentTarget, {
            presetId: dateRange.presetId,
            from: dateRange.from,
            to: dateRange.to,
        });
    }

    function handleIntervalApply({ presetId, from, to }) {
        if (presetId && presetId !== 'custom') {
            applyPreset(presetId);
        } else {
            applyCustomRange(from, to);
        }
    }

    const presetLabelKey = DATE_INTERVAL_PRESET_MAP[dateRange.presetId]?.labelKey;
    const dateLabel = dateRange.presetId === 'custom'
        ? `${toDisplayDate(dateRange.from)} — ${toDisplayDate(dateRange.to)}`
        : (presetLabelKey ? t(presetLabelKey) : t('common:actions.select'));

    const planOptions = plans.map(plan => ({
        id: plan.id,
        label: plan.label,
        searchLabel: plan.label,
        optionContent: (
            <span className="insights-plan-option">
                <Text.span contrast={plan.deleted_at ? 5 : 8}>
                    {plan.label}
                </Text.span>
                {plan.deleted_at && (
                    <span className="insights-plan-option__deleted-badge">
                        <Text.span contrast={5}>{t('insights.filters.planDeleted')}</Text.span>
                    </span>
                )}
            </span>
        ),
    }));

    return (
        <div className="insights-filters">
            <Text.span contrast={5}>{t('insights.filters.label')}</Text.span>

            <ChipPicker
                title={t('insights.filters.period')}
                valueLabel={dateLabel}
                filled
                onClick={handleDateChipClick}
            />

            <DatePicker.Interval
                controlRef={intervalPickerRef}
                value={dateRange}
                presets={datePresets}
                maxDate={new Date()}
                onApply={handleIntervalApply}
            />

            <ChipPicker
                title={t('common:entities.plans')}
                multiple
                activateOnHover
                value={planIds}
                options={planOptions}
                onChange={setPlanIds}
                searchable
            />

            <ChipPicker
                title={t('common:fields.status')}
                multiple
                activateOnHover
                value={chargeStatuses}
                options={statusOptions}
                onChange={setChargeStatuses}
            />

            <ChipPicker
                title={t('insights.filters.method')}
                multiple
                activateOnHover
                value={chargeMethods}
                options={methodOptions}
                onChange={setChargeMethods}
            />

            <ChipPicker
                title={t('insights.filters.granularity')}
                activateOnHover
                valueLabel={formatGranularity(t, granularity)}
                customContent={({ close }) => (
                    <GranularityPopperContent
                        value={granularity}
                        onApply={(next) => {
                            setGranularity(next);
                            close();
                        }}
                    />
                )}
            />

            {hasOptionalFilters && (
                <Button
                    color="overlay"
                    size="small"
                    startIcon={<IconFilterOff size={16} stroke={2} />}
                    text={t('insights.filters.clear')}
                    onClick={clearOptionalFilters}
                    preventLoading
                />
            )}
        </div>
    );
}
